import {mapJournalEvent} from '../events/events.models';
import {mapStockFacts} from '../shared/components/sm-stock-summary/stock-summary.model';
import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {catchError, map, Observable, throwError} from 'rxjs';
import {ApiResponse} from '../auth/auth.models';
import {buildApiUrl} from '../shared/api/api-url';
import type {
  HomeDashboardData,
  HomeDashboardRecentChange,
  HomeDashboardRecentChangeDto,
  HomeDashboardRecentChangePathSegment,
  HomeDashboardRecentChangePathSegmentDto,
  HomeDashboardRecentChangeType,
  HomeDashboardResponseDto,
  NearExpiryReportData,
  NearExpiryReportItem,
  NearExpiryReportItemDto,
  NearExpiryReportPathSegment,
  NearExpiryReportResponseDto,
} from './reports.models';

function toPathSegment(dto: NearExpiryReportPathSegment): NearExpiryReportPathSegment {
  return {
    id: dto.id,
    name: dto.name,
  };
}

function toReportItem(dto: NearExpiryReportItemDto): NearExpiryReportItem {
  return {
    isFavorite: dto.isFavorite,
    canEdit: dto.canEdit === true,
    description: dto.description ?? null,
    expirationDate: dto.expirationDate ?? null,
    expirationStatus: dto.expirationStatus,
    id: dto.id,
    imageUrl: dto.imageUrl ?? null,
    locationId: dto.locationId,
    locationLabel: dto.locationLabel ?? null,
    name: dto.name,
    path: (dto.path ?? []).map(toPathSegment),
    noticeBeforeDays: dto.noticeBeforeDays ?? null,
    ...mapStockFacts(dto),
    quantity: dto.quantity,
  };
}

function toHomeDashboardRecentChangePathSegment(dto: HomeDashboardRecentChangePathSegmentDto): HomeDashboardRecentChangePathSegment {
  return {
    id: dto.id,
    name: dto.name,
  };
}

function toHomeDashboardRecentChangeType(value: string | null | undefined): HomeDashboardRecentChangeType {
  switch (value?.trim().toLowerCase()) {
    case 'added':
      return 'added';
    case 'updated':
      return 'updated';
    default:
      return 'unknown';
  }
}

function toHomeDashboardRecentChange(dto: HomeDashboardRecentChangeDto): HomeDashboardRecentChange {
  return {
    ...mapStockFacts(dto),
    quantity: dto.quantity ?? null,
    changeType: toHomeDashboardRecentChangeType(dto.changeType),
    containerId: dto.containerId ?? dto.locationId ?? null,
    containerLabel: dto.containerLabel ?? dto.locationLabel ?? null,
    isFavorite: dto.isFavorite,
    canEdit: dto.canEdit === true,
    description: dto.description ?? null,
    entityType: dto.entityType,
    id: dto.id,
    imageUrl: dto.imageUrl ?? null,
    locationId: dto.locationId ?? dto.containerId ?? null,
    locationLabel: dto.locationLabel ?? dto.containerLabel ?? null,
    name: dto.name,
    path: (dto.path ?? []).map(toHomeDashboardRecentChangePathSegment),
    timestamp: dto.timestamp,
  };
}

@Injectable({
  providedIn: 'root',
})
export class ReportsService {
  constructor(private readonly httpClient: HttpClient) {}

  getNearExpiryReport(timeZoneId?: string): Observable<NearExpiryReportData> {
    return this.httpClient.get<ApiResponse<NearExpiryReportResponseDto>>(
      buildApiUrl('/v1/reports/near-expiry'),
      timeZoneId ? {headers: {'X-Time-Zone': timeZoneId}} : undefined,
    ).pipe(
      map((payload) => {
        if (!payload.data) {
          throw new Error('Near expiry report response is missing.');
        }

        return {
          items: payload.data.items.map(toReportItem),
        };
      }),
      catchError((error) => throwError(() => error)),
    );
  }

  getRecentChangesReport(): Observable<HomeDashboardData> {
    return this.httpClient.get<ApiResponse<HomeDashboardResponseDto>>(
      buildApiUrl('/v1/home/dashboard'),
    ).pipe(
      map((payload) => {
        if (!payload.data) {
          throw new Error('Home dashboard response is missing.');
        }

        return {
          recentChanges: (payload.data.recentChanges ?? []).map(toHomeDashboardRecentChange),
          recentEvents: (payload.data.recentEvents ?? []).map(mapJournalEvent),
        };
      }),
      catchError((error) => throwError(() => error)),
    );
  }
}
